import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ArrowDown, ArrowLeft, ArrowRight, ArrowUp, Aperture, Blend, Camera,
  ChevronsRight, Moon, MoveRight, Plus, Sun, Wind, ZoomIn, ZoomOut, Trash2,
  Shuffle, CopyCheck, Sparkles,
} from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { Checkbox } from '@/components/ui/checkbox';
import ProBadge from '@/components/ProBadge';
import {
  TRANSITION_KINDS,
  isDirectionalKind,
  clampTransitionDuration,
  DEFAULT_TRANSITION_DURATION_SEC,
  MIN_TRANSITION_DURATION_SEC,
  MAX_TRANSITION_DURATION_SEC,
  type SceneTransition,
  type TransitionDirection,
  type TransitionKind,
} from '@/lib/transitions/types';

export const TRANSITION_KIND_ICONS: Record<TransitionKind, React.ComponentType<{ className?: string }>> = {
  'camera-move': Camera,
  'zoom-in': ZoomIn,
  'zoom-out': ZoomOut,
  'whip-pan': Wind,
  'dip-to-black': Moon,
  'dip-to-white': Sun,
  blur: Aperture,
  crossfade: Blend,
  slide: MoveRight,
  wipe: ChevronsRight,
};

const DIRECTIONS: Array<{ value: TransitionDirection; Icon: React.ComponentType<{ className?: string }> }> = [
  { value: 'left', Icon: ArrowLeft },
  { value: 'right', Icon: ArrowRight },
  { value: 'up', Icon: ArrowUp },
  { value: 'down', Icon: ArrowDown },
];

export const TransitionBadge: React.FC<{
  cutIndex: number;
  transition: SceneTransition | null;
  onChange: (transition: SceneTransition | null) => void;
  onApplyToAll: (transition: SceneTransition) => void;
  onRandomize: (kinds: TransitionKind[]) => void;
  /** Restrict the picker grid + randomize list to this subset (e.g. the
   *  transform family on the crop-keyframe track). Defaults to all kinds. */
  kinds?: TransitionKind[];
  /** Keep embedded controls discoverable on touch devices without hover. */
  alwaysVisible?: boolean;
  compact?: boolean;
  /** Signal-driven generator. Omit it entirely and the Auto block does not
   *  render at all — which is what every surface that has no video signals
   *  wiring (e.g. the crop-keyframe track) gets. */
  onAuto?: () => void;
  /** When set, the Auto button is disabled and this string is shown as the
   *  reason. Rendered as an explanatory note, never as an error. */
  autoDisabledReason?: string | null;
  /** Unobtrusive "Detected: X" line so the user understands why Auto picked
   *  what it picked. */
  autoContentTypeLabel?: string | null;
}> = ({
  cutIndex, transition, onChange, onApplyToAll, onRandomize,
  kinds = TRANSITION_KINDS as unknown as TransitionKind[],
  alwaysVisible = false, compact = false,
  onAuto, autoDisabledReason = null, autoContentTypeLabel = null,
}) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [randomKinds, setRandomKinds] = useState<Set<TransitionKind>>(
    () => new Set(kinds),
  );

  const Icon = transition ? TRANSITION_KIND_ICONS[transition.kind] : Plus;

  const setKind = (kind: TransitionKind) => {
    onChange({
      afterSceneIndex: cutIndex,
      kind,
      durationSec: transition?.durationSec ?? DEFAULT_TRANSITION_DURATION_SEC,
      ...(isDirectionalKind(kind) ? { direction: transition?.direction ?? 'left' } : {}),
    });
  };

  const toggleRandomKind = (kind: TransitionKind) => {
    setRandomKinds((prev) => {
      const next = new Set(prev);
      if (next.has(kind)) next.delete(kind);
      else next.add(kind);
      return next;
    });
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={`transition-badge-${cutIndex}`}
          className={`absolute z-40 flex h-6 w-6 -translate-x-1/2 items-center justify-center rounded-full border shadow-sm transition-colors ${compact && !open ? "opacity-0 group-hover/cut:opacity-100 hover:opacity-100 focus-visible:opacity-100" : ""} ${
            transition
              ? 'border-primary bg-primary text-primary-foreground'
              : `border-border bg-background/90 text-muted-foreground ${alwaysVisible || open ? '' : 'opacity-0 group-hover/cut:opacity-100 hover:opacity-100 focus-visible:opacity-100'}`
          }`}
          // The badge floats over the scene bars: never let its pointerdown
          // start a bar move/resize drag, and keep right-click for the
          // timeline context menu.
          onPointerDown={(e) => { if (e.button !== 0) return; e.stopPropagation(); }}
          onClick={(e) => e.stopPropagation()}
          title={t('transitions.title')}
        >
          <Icon className="h-3.5 w-3.5" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-72" align="center" side="top" data-layer-ui>
        <div className="mb-2 flex items-center justify-between">
          <span className="text-xs font-semibold">{t('transitions.title')}</span>
          <ProBadge />
        </div>
        <div className="grid grid-cols-2 gap-1">
          {kinds.map((kind) => {
            const KindIcon = TRANSITION_KIND_ICONS[kind];
            const selected = transition?.kind === kind;
            return (
              <Button
                key={kind}
                type="button"
                variant={selected ? 'default' : 'outline'}
                size="sm"
                className="h-8 justify-start gap-2 text-xs"
                data-testid={`transition-kind-${kind}`}
                onClick={() => setKind(kind)}
              >
                <KindIcon className="h-3.5 w-3.5" />
                {t(`transitions.kinds.${kind}`)}
              </Button>
            );
          })}
        </div>

        <Button
          type="button"
          variant="outline"
          size="sm"
          className="mt-2 h-8 w-full gap-1 text-xs"
          data-testid="transition-apply-all"
          disabled={!transition}
          onClick={() => { if (transition) onApplyToAll(transition); }}
        >
          <CopyCheck className="h-3.5 w-3.5" />
          {t('transitions.addToAllScenes')}
        </Button>

        {transition && (
          <div className="mt-3 space-y-2">
            <div className="flex items-center gap-2">
              <span className="w-20 text-xs text-muted-foreground">{t('transitions.duration')}</span>
              <Slider
                min={MIN_TRANSITION_DURATION_SEC}
                max={MAX_TRANSITION_DURATION_SEC}
                step={0.05}
                value={[transition.durationSec]}
                className="flex-1"
                onValueChange={([v]) => {
                  if (!Number.isFinite(v)) return;
                  onChange({ ...transition, durationSec: clampTransitionDuration(v) });
                }}
              />
              <span className="w-10 shrink-0 text-right text-xs tabular-nums text-muted-foreground">
                {transition.durationSec.toFixed(2)}s
              </span>
            </div>
            {isDirectionalKind(transition.kind) && (
              <div className="flex items-center gap-2">
                <span className="w-20 text-xs text-muted-foreground">{t('transitions.direction')}</span>
                <div className="flex gap-1">
                  {DIRECTIONS.map(({ value, Icon: DirIcon }) => (
                    <Button
                      key={value}
                      type="button"
                      size="sm"
                      variant={(transition.direction ?? 'left') === value ? 'default' : 'outline'}
                      className="h-8 w-8 p-0"
                      onClick={() => onChange({ ...transition, direction: value })}
                    >
                      <DirIcon className="h-3.5 w-3.5" />
                    </Button>
                  ))}
                </div>
              </div>
            )}
            <Button
              type="button" variant="ghost" size="sm" className="h-8 w-full gap-1 text-xs"
              data-testid="transition-remove"
              onClick={() => onChange(null)}
            >
              <Trash2 className="h-3.5 w-3.5" />
              {t('transitions.remove')}
            </Button>
          </div>
        )}

        <div className="mt-3 border-t pt-2">
          <span className="text-xs font-semibold">{t('transitions.randomize')}</span>
          <p className="mb-1 text-[11px] text-muted-foreground">{t('transitions.randomizeHint')}</p>
          <div className="grid max-h-28 grid-cols-2 gap-1 overflow-y-auto">
            {kinds.map((kind) => (
              <label key={kind} className="flex items-center gap-1.5 text-xs">
                <Checkbox
                  checked={randomKinds.has(kind)}
                  onCheckedChange={() => toggleRandomKind(kind)}
                />
                {t(`transitions.kinds.${kind}`)}
              </label>
            ))}
          </div>
          <div className="mt-2 flex gap-2">
            <Button
              type="button" variant="secondary" size="sm"
              className="h-8 flex-1 gap-1 text-xs"
              data-testid="transition-randomize"
              disabled={randomKinds.size === 0}
              onClick={() => { onRandomize([...randomKinds]); setOpen(false); }}
            >
              <Shuffle className="h-3.5 w-3.5" />
              {t('transitions.randomize')}
            </Button>
            {onAuto && (
              <Button
                type="button" variant="secondary" size="sm"
                className="h-8 flex-1 gap-1 text-xs"
                data-testid="transition-auto"
                disabled={!!autoDisabledReason}
                title={autoDisabledReason ?? t('transitions.autoHint')}
                onClick={() => { onAuto(); setOpen(false); }}
              >
                <Sparkles className="h-3.5 w-3.5" />
                {t('transitions.auto')}
              </Button>
            )}
          </div>
          {onAuto && (
            <p className="mt-1 text-[11px] text-muted-foreground" data-testid="transition-auto-note">
              {autoDisabledReason ?? autoContentTypeLabel ?? t('transitions.autoHint')}
            </p>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};
